import React from "react";
import { Layout, Space, Switch, Button, Typography, Tooltip, Badge } from "antd";
import {
  SunOutlined,
  MoonOutlined,
  LogoutOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/useAuth";
import { getQbStatus } from "../../api/quickbooks";

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  isDark: boolean;
  onToggleTheme: () => void;
}

/**
 * Top application header containing user info, QB connection status,
 * theme toggle, and logout button.
 */
export function Header({ isDark, onToggleTheme }: HeaderProps): React.JSX.Element {
  const { user, logout } = useAuth();

  const { data: qbStatus } = useQuery({
    queryKey: ["qbStatus"],
    queryFn: getQbStatus,
    refetchInterval: 60_000,
    retry: false,
  });

  const isConnected = qbStatus?.connected ?? false;

  return (
    <AntHeader
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingInline: 24,
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: isDark
          ? "0 1px 4px rgba(0,0,0,0.4)"
          : "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      {/* Left: App title */}
      <Text strong style={{ fontSize: 18 }}>
        QB Financial Dashboard
      </Text>

      {/* Right: Controls */}
      <Space size="middle" align="center">
        {/* QuickBooks connection indicator */}
        <Tooltip
          title={
            isConnected
              ? `QuickBooks connected${qbStatus?.realmId ? ` (Realm: ${qbStatus.realmId})` : ""}`
              : "QuickBooks not connected"
          }
        >
          <Space size={6} style={{ cursor: "default" }}>
            <Badge
              status={isConnected ? "success" : "error"}
              style={{ lineHeight: 1 }}
            />
            <Text
              style={{
                fontSize: 13,
                color: isConnected ? "#52c41a" : "#ff4d4f",
              }}
            >
              {isConnected ? "QB Connected" : "QB Disconnected"}
            </Text>
          </Space>
        </Tooltip>

        {/* Theme toggle */}
        <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"}>
          <Switch
            checked={isDark}
            onChange={onToggleTheme}
            checkedChildren={<MoonOutlined />}
            unCheckedChildren={<SunOutlined />}
          />
        </Tooltip>

        {/* User info */}
        {user && (
          <Space size={6}>
            <UserOutlined style={{ fontSize: 16 }} />
            <Text style={{ fontSize: 13 }}>{user.name}</Text>
          </Space>
        )}

        {/* Logout */}
        <Tooltip title="Sign out">
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={logout}
            size="small"
            aria-label="Sign out"
          />
        </Tooltip>
      </Space>
    </AntHeader>
  );
}
